import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  useToken,
} from '@chakra-ui/react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useBrandPalette } from '../../../contexts/BrandPaletteContext';
import { PrimaryButton } from '../../../components/ui';
import { spectrumColors } from '../../../theme/colors';
import { HubGhostButton, HubPanel, hub } from './hubUi';

export const CHART_SLICES = [
  { id: 'all', label: 'All fleet', listFilter: 'all' },
  { id: 'available', label: 'Available', listFilter: 'all' },
  { id: 'deployed', label: 'Deployed', listFilter: 'all' },
  { id: 'pendingKyc', label: 'Pending', listFilter: 'pending' },
  { id: 'rejected', label: 'Rejected', listFilter: 'rejected' },
];

const FILL_CLASSIC = ['#D97706', '#B45309', '#92400E', '#F59E0B', '#C2410C'];
const FILL_LOGO = ['#1E7AD8', '#E87B20', '#0B5CAD', '#F0A35A', '#155FA8'];
const FILL_CLARITY = ['#008060', '#2C6ECB', '#004C3F', '#5C6AC4', '#47C1BF'];
const FILL_SPECTRUM = [
  spectrumColors.resources,
  spectrumColors.logistics,
  spectrumColors.diversity,
  spectrumColors.energy,
  '#3E8CC4',
  '#008552',
  '#004A82',
];

function chartFills(paletteId) {
  if (paletteId === 'spectrum') return FILL_SPECTRUM;
  if (paletteId === 'logoBrand') return FILL_LOGO;
  if (paletteId === 'matchClarity') return FILL_CLARITY;
  return FILL_CLASSIC;
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <Box
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="10px"
      px={3}
      py={2}
      boxShadow="sm"
    >
      <Text fontSize="13px" fontWeight="700" color="text.primary">
        {row.typeName}
      </Text>
      <Text fontSize="12px" color="text.secondary">
        {row.count} · click to open list
      </Text>
    </Box>
  );
}

/**
 * Horizontal fleet-by-type chart for Hub Equipment.
 * Data from GET /dashboard → equipment.byType / byTypeSlices.
 */
export default function HubFleetTypeChart({
  byType = [],
  sliceLabel = 'All fleet',
  chartSlice = 'all',
  sliceCounts = {},
  onSliceChange,
  onOpenType,
  onOpenAll,
}) {
  const { paletteId } = useBrandPalette();
  const fills = useMemo(() => chartFills(paletteId), [paletteId]);
  const [axisToken] = useToken('colors', ['text.secondary']);
  const axisFallback = useColorModeValue('#5C6570', '#A8A49A');
  const axisColor = axisToken || axisFallback;
  const gridStroke = useColorModeValue('#E8ECF0', '#2A3038');

  const rows = useMemo(() => {
    return (Array.isArray(byType) ? byType : [])
      .map((row, i) => ({
        typeName: row.typeName || 'Unspecified',
        count: Number(row.count) || 0,
        fill: fills[i % fills.length],
      }))
      .filter((r) => r.count > 0);
  }, [byType, fills]);

  const chartH = Math.max(240, Math.min(520, rows.length * 36 + 56));
  const xMax = Math.max(1, ...rows.map((r) => r.count));

  return (
    <HubPanel minW={0} w="100%" overflow="hidden">
      <Flex
        align={{ base: 'stretch', md: 'center' }}
        justify="space-between"
        gap={3}
        mb={4}
        w="100%"
        flexWrap="wrap"
      >
        <Text fontWeight="600" fontSize="15px" color="text.primary">
          Fleet by type · {sliceLabel}
        </Text>
        <Flex
          align="center"
          gap={1.5}
          flexWrap="wrap"
          justify={{ base: 'flex-start', md: 'flex-end' }}
        >
          {CHART_SLICES.map((s) => {
            const selected = chartSlice === s.id;
            const label = `${s.label} (${sliceCounts[s.id] ?? 0})`;
            const chipSx = {
              h: '28px',
              minH: '28px',
              px: 2.5,
              fontSize: '12px',
              fontWeight: '600',
              borderRadius: 'full',
            };
            if (selected) {
              return (
                <PrimaryButton
                  key={s.id}
                  size="xs"
                  onClick={() => onSliceChange?.(s.id)}
                  sx={chipSx}
                >
                  {label}
                </PrimaryButton>
              );
            }
            return (
              <HubGhostButton
                key={s.id}
                size="xs"
                onClick={() => onSliceChange?.(s.id)}
                sx={chipSx}
              >
                {label}
              </HubGhostButton>
            );
          })}
          {onOpenAll ? (
            <HubGhostButton
              size="xs"
              onClick={onOpenAll}
              sx={{
                h: '28px',
                minH: '28px',
                px: 2.5,
                fontSize: '12px',
                fontWeight: '600',
                borderRadius: 'full',
              }}
            >
              Open list →
            </HubGhostButton>
          ) : null}
        </Flex>
      </Flex>

      {rows.length === 0 ? (
        <Flex
          align="center"
          justify="center"
          minH="180px"
          borderRadius="12px"
          bg={hub.softBg}
        >
          <Text fontSize="14px" color={hub.muted}>
            No equipment types in this group
          </Text>
        </Flex>
      ) : (
        <Box w="100%" minW={0} h={`${chartH}px`}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 4, right: 36, bottom: 12, left: 8 }}
              barCategoryGap="18%"
            >
              <CartesianGrid
                horizontal={false}
                stroke={gridStroke}
                strokeDasharray="3 6"
              />
              <XAxis
                type="number"
                domain={[0, Math.ceil(xMax * 1.08)]}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                tick={{ fill: axisColor, fontSize: 11 }}
                label={{
                  value: 'Count in fleet',
                  position: 'insideBottom',
                  offset: -4,
                  style: { fill: axisColor, fontSize: 11 },
                }}
              />
              <YAxis
                type="category"
                dataKey="typeName"
                width={132}
                axisLine={false}
                tickLine={false}
                tick={{ fill: axisColor, fontSize: 12, fontWeight: 600 }}
              />
              <Tooltip
                cursor={{ fill: gridStroke, opacity: 0.35 }}
                content={<ChartTooltip />}
              />
              <Bar
                dataKey="count"
                radius={[0, 8, 8, 0]}
                maxBarSize={28}
                background={{ fill: gridStroke, opacity: 0.35, radius: 8 }}
                cursor="pointer"
                onClick={(entry) => {
                  const name =
                    entry?.payload?.typeName ||
                    entry?.typeName ||
                    entry?.name;
                  if (name && onOpenType) onOpenType(name);
                }}
              >
                {rows.map((row) => (
                  <Cell key={row.typeName} fill={row.fill} />
                ))}
                <LabelList
                  dataKey="count"
                  position="right"
                  style={{
                    fill: axisColor,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </HubPanel>
  );
}
